import { stAnalytics } from "@storytell/analytics";
import { type Auth, timestampToDate, useCallOptions, useTenancyClient, withBearerToken } from "@storytell/cp-client";
import { PlanType } from "@storytell/cp-client/src/gen/tenancy/v3/tenancy_pb";
import dayjs from "dayjs";
import { createEffect, createResource, createSignal } from "solid-js";

export type PlanTypes = "PLAN_TYPE_PRO_V1" | "PLAN_TYPE_FREE_V1";
export type BillingService = ReturnType<typeof createBillingService>;

// TODO:  Add refetch function to fetch all data again
export const createBillingService = ({
  authReady,
  authToken,
  authUserId,
  authRegistered,
  hasChats,
  userId,
}: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  authUserId: () => string | undefined;
  authRegistered: () => boolean;
  hasChats: () => boolean;
  userId: () => string | undefined;
}) => {
  const tenancyClient = useTenancyClient();
  const [show, setShow] = createSignal(false);

  const [data, { refetch, mutate }] = createResource(
    () => authReady() && userId() && (authRegistered() || hasChats()),
    async () => {
      setShow(true);
      const res = await tenancyClient.getTenancyInfo({}, useCallOptions(withBearerToken(authToken())));

      if (res.isOk()) {
        const { balance, plan } = res.value.tenancyInfo;
        return {
          balance: {
            ...balance,
            lastUpdated: timestampToDate(balance.lastUpdated),
            credits: Number(balance.credits),
            creditsAddedThisPeriod: Number(balance.creditsAddedThisPeriod),
            creditsUsedThisPeriod: Number(balance.creditsUsedThisPeriod),
          },
          plan: {
            ...plan,
            createdAt: timestampToDate(plan.createdAt),
            modifiedAt: timestampToDate(plan.modifiedAt),
            type: plan.type === PlanType.PRO_V1 ? "PLAN_TYPE_PRO_V1" : "PLAN_TYPE_FREE_V1",
          },
        };
      }

      return undefined;
    },
  );

  const credits = () => data()?.balance.credits ?? 0;

  const updateCredits = (delta: number, lastUpdated: string) => {
    const isAfter = dayjs(lastUpdated).isAfter(data()?.balance.lastUpdated ?? new Date(0));
    if (!isAfter) return;

    mutate((d) =>
      d
        ? {
            ...d,
            balance: {
              ...d.balance,
              credits: d.balance.credits + delta,
              creditsAddedThisPeriod: d.balance.credits + delta,
              lastUpdated: new Date(lastUpdated),
            },
          }
        : undefined,
    );
  };

  const isProPlan = () => data()?.plan.type === "PLAN_TYPE_PRO_V1";
  const isFreePlan = () => data()?.plan.type === "PLAN_TYPE_FREE_V1";
  const loading = () => data.loading;
  const creditsUsedThisPeriod = () => data()?.balance.creditsUsedThisPeriod ?? 0;

  createEffect(() => {
    const d = data.latest;
    const id = authUserId();
    if (!d || !id) return;

    stAnalytics.identify(id, {
      planType: d.plan.type,
      creditBalance: d.balance.credits,
    });
  });

  return {
    credits,
    updateCredits,
    isProPlan,
    isFreePlan,
    refetch,
    loading,
    creditsUsedThisPeriod,
    show: () => data.latest !== undefined || show(),
  };
};
